import React, { useState, useRef, useEffect } from 'react';
import { Link, useLocation, useNavigate } from 'react-router-dom';
import { useAuth } from '../../context/AuthContext';
import { siteConfig } from "../../config/site"
import { Button } from '../common/Button';
import { useFeedback } from '../../utils/feedback';
import Feedback from '../common/Feedback';
import { useOnClickOutside } from '../../hooks/useOnClickOutside';
import { Select } from '../common/Select';
import { DollarSign } from 'lucide-react';
import { CURRENCIES } from '../../config/currencies';
import api from '../../config/api';
import endpoints from '../../config/endpoints';

const getFirstName = (fullName) => {
    if (!fullName) return 'Guest';
    return fullName.split(' ')[0];
};

export const Header = () => {
    const { isAuthenticated, user, logout, updateUserProfile } = useAuth();
    const [isMobileMenuOpen, setIsMobileMenuOpen] = useState(false);
    const [isProfileOpen, setIsProfileOpen] = useState(false);
    const [isClosing, setIsClosing] = useState(false);
    const dropdownRef = useRef(null);
    const { handleFeedbackSubmit } = useFeedback();
    const location = useLocation();
    const navigate = useNavigate();
    const [selectedCurrency, setSelectedCurrency] = useState(user?.preferences?.currency || 'USD');

    // Close dropdown when clicking outside
    useOnClickOutside(dropdownRef, () => setIsProfileOpen(false));

    // Update currency when user changes
    useEffect(() => {
        if (user?.preferences?.currency) {
            setSelectedCurrency(user.preferences.currency);
        }
    }, [user]);

    // Add handler for currency change
    const handleCurrencyChange = async (e) => {
        const newCurrency = e.target.value;
        setSelectedCurrency(newCurrency);

        try {
            const requestPayload = {
                currency: newCurrency,
            };

            await api.put(endpoints.user.updatePreferences, requestPayload);
            await updateUserProfile();
        } catch (error) {
            console.error('Failed to update currency:', error);
            // Revert to previous currency on error
            setSelectedCurrency(user?.preferences?.currency || 'USD');
        }
    };

    const handleToggleMenu = () => {
        setIsMobileMenuOpen(prev => !prev);
    };

    const publicNavLinks = [
        { to: "/why-us", text: "Why Us" },
        { to: "/about", text: "About" },
        { to: "/contact", text: "Contact" },
        // Uncomment if pricing page is ready
        // { to: "/pricing", text: "Pricing" },
    ];

    const privateNavLinks = [
        { to: "/dashboard", text: "Dashboard" },
        { to: "/subscriptions", text: "Subscriptions" },
        { to: "/analytics", text: "Analytics" },
    ];

    // Modify navLinks to only show private links in desktop view
    const navLinks = isAuthenticated ? privateNavLinks : publicNavLinks;

    // Helper function to check if link is active
    const isActiveLink = (path) => {
        if (path === '/subscriptions' && location.pathname === '/') {
            return true;
        }
        return location.pathname === path;
    };

    const handleMenuClose = (callback) => {
        setIsClosing(true);
        // Wait for animation to complete before executing callback
        setTimeout(() => {
            setIsMobileMenuOpen(false);
            setIsClosing(false);
            if (callback) callback();
        }, 300); // Match this with your CSS transition duration
    };

    const handleNavigation = (to) => {
        handleMenuClose(() => navigate(to));
    };

    // Helper to find currency symbol
    const getCurrencySymbol = (code) => {
        const currency = CURRENCIES.find(c => c.code === code);
        return currency ? currency.symbol : '$';
    };

    return (
        <header className="bg-white shadow-sm border-b border-gray-200 fixed w-full z-10">
            <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8">
                <div className="flex justify-between items-center h-16">
                    {/* Logo and navigation */}
                    <div className="flex items-center">
                        <Link to="/subscriptions" className="text-xl font-bold text-blue-600">
                            {siteConfig.name}
                        </Link>
                        {/* Updated Desktop Navigation - adjusted breakpoint and spacing */}
                        <nav className="hidden lg:ml-8 lg:flex lg:space-x-4">
                            {navLinks.map((link) => (
                                <Link
                                    key={link.to}
                                    to={link.to}
                                    className={`relative px-2 py-2 text-sm text-gray-600 hover:text-gray-900 transition-colors duration-200 ${
                                        isActiveLink(link.to)
                                            ? 'text-blue-600 font-semibold after:absolute after:bottom-0 after:left-0 after:w-full after:h-0.5 after:bg-blue-600'
                                            : ''
                                    }`}
                                >
                                    {link.text}
                                </Link>
                            ))}
                        </nav>
                    </div>

                    {/* Updated mobile menu button - show on tablet */}
                    <button
                        type="button"
                        className="lg:hidden inline-flex items-center justify-center p-2 rounded-md text-gray-600 hover:text-gray-900 hover:bg-gray-100 focus:outline-none focus:ring-2 focus:ring-inset focus:ring-blue-500"
                        onClick={handleToggleMenu}
                        aria-controls="mobile-menu"
                        aria-expanded={isMobileMenuOpen}
                    >
                        <span className="sr-only">Open main menu</span>
                        {/* Icon when menu is closed */}
                        <svg
                            className={`${isMobileMenuOpen ? 'hidden' : 'block'} h-6 w-6`}
                            fill="none"
                            viewBox="0 0 24 24"
                            strokeWidth="1.5"
                            stroke="currentColor"
                        >
                            <path strokeLinecap="round" strokeLinejoin="round" d="M3.75 6.75h16.5M3.75 12h16.5m-16.5 5.25h16.5" />
                        </svg>
                        {/* Icon when menu is open */}
                        <svg
                            className={`${isMobileMenuOpen ? 'block' : 'hidden'} h-6 w-6`}
                            fill="none"
                            viewBox="0 0 24 24"
                            strokeWidth="1.5"
                            stroke="currentColor"
                        >
                            <path strokeLinecap="round" strokeLinejoin="round" d="M6 18L18 6M6 6l12 12" />
                        </svg>
                    </button>

                    {/* Auth section - adjusted breakpoint */}
                    <div className="hidden lg:flex items-center">
                        {isAuthenticated ? (
                            <>
                                {/* Currency selector and other authenticated content */}
                                <div className="flex items-center space-x-6">
                                    <div className="flex items-center space-x-2">
                                        <Select
                                            value={selectedCurrency}
                                            onChange={handleCurrencyChange}
                                            className="w-28 text-sm"
                                            aria-label="Select currency"
                                        >
                                            {CURRENCIES.map(currency => (
                                                <option key={currency.code} value={currency.code}>
                                                    {currency.code} ({currency.symbol})
                                                </option>
                                            ))}
                                        </Select>
                                    </div>

                                    <Feedback
                                        position="right"
                                        prompt="Help us improve our platform"
                                        source="header_feedback"
                                        onSubmit={handleFeedbackSubmit}
                                    />

                                    {!user?.is_anonymous ? (
                                        <div className="relative" ref={dropdownRef}>
                                            <button
                                                onClick={() => setIsProfileOpen(!isProfileOpen)}
                                                className="flex items-center space-x-3 group p-2 rounded-lg hover:bg-gray-50 transition-colors duration-200"
                                                aria-expanded={isProfileOpen}
                                                aria-haspopup="true"
                                            >
                                                <div className="h-8 w-8 rounded-full bg-blue-100 flex items-center justify-center">
                                                    <span className="text-blue-600 font-medium">
                                                        {getFirstName(user?.name)?.charAt(0) || 'G'}
                                                    </span>
                                                </div>
                                                <span className="text-sm font-medium text-gray-700 group-hover:text-gray-900">
                                                    {getFirstName(user?.name)}
                                                </span>
                                                <svg
                                                    className={`h-5 w-5 text-gray-400 transition-transform duration-200 ${isProfileOpen ? 'rotate-180' : ''}`}
                                                    fill="none"
                                                    viewBox="0 0 24 24"
                                                    stroke="currentColor"
                                                >
                                                    <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M19 9l-7 7-7-7" />
                                                </svg>
                                            </button>

                                            {/* Dropdown Menu */}
                                            {isProfileOpen && (
                                                <div className="absolute right-0 mt-2 w-48 bg-white rounded-lg shadow-lg py-1 border border-gray-100">
                                                    {/* User Info Section */}
                                                    <div className="px-4 py-2 border-b border-gray-100">
                                                        <p className="text-sm font-medium text-gray-900">{user?.name}</p>
                                                        <p className="text-sm text-gray-500 truncate">{user?.email}</p>
                                                    </div>

                                                    {/* Account Section */}
                                                    <div className="py-1">
                                                        <Link
                                                            to="/settings"
                                                            onClick={() => setIsProfileOpen(false)}
                                                            className="block px-4 py-2 text-sm text-gray-700 hover:bg-gray-50 transition-colors duration-200"
                                                        >
                                                            Settings
                                                        </Link>
                                                    </div>

                                                    {/* Resources Section */}
                                                    <div className="py-1 border-t border-gray-100">
                                                        <div className="px-4 py-2">
                                                            <span className="text-xs font-medium text-gray-500 uppercase">Resources</span>
                                                        </div>
                                                        <Link
                                                            to="/why-us"
                                                            onClick={() => setIsProfileOpen(false)}
                                                            className="block px-4 py-2 text-sm text-gray-700 hover:bg-gray-50 transition-colors duration-200"
                                                        >
                                                            Why Us
                                                        </Link>
                                                        <Link
                                                            to="/about"
                                                            onClick={() => setIsProfileOpen(false)}
                                                            className="block px-4 py-2 text-sm text-gray-700 hover:bg-gray-50 transition-colors duration-200"
                                                        >
                                                            About
                                                        </Link>
                                                        <Link
                                                            to="/contact"
                                                            onClick={() => setIsProfileOpen(false)}
                                                            className="block px-4 py-2 text-sm text-gray-700 hover:bg-gray-50 transition-colors duration-200"
                                                        >
                                                            Contact
                                                        </Link>
                                                    </div>

                                                    {/* Legal Section */}
                                                    <div className="py-1 border-t border-gray-100">
                                                        <div className="px-4 py-2">
                                                            <span className="text-xs font-medium text-gray-500 uppercase">Legal</span>
                                                        </div>
                                                        <Link
                                                            to="/terms"
                                                            onClick={() => setIsProfileOpen(false)}
                                                            className="block px-4 py-2 text-sm text-gray-700 hover:bg-gray-50 transition-colors duration-200"
                                                        >
                                                            Terms of Service
                                                        </Link>
                                                        <Link
                                                            to="/privacy"
                                                            onClick={() => setIsProfileOpen(false)}
                                                            className="block px-4 py-2 text-sm text-gray-700 hover:bg-gray-50 transition-colors duration-200"
                                                        >
                                                            Privacy Policy
                                                        </Link>
                                                    </div>

                                                    {/* Sign Out Section */}
                                                    <div className="py-1 border-t border-gray-100">
                                                        <button
                                                            onClick={() => {
                                                                logout();
                                                                setIsProfileOpen(false);
                                                            }}
                                                            className="block w-full text-left px-4 py-2 text-sm text-red-600 hover:bg-red-50 transition-colors duration-200"
                                                        >
                                                            Sign out
                                                        </button>
                                                    </div>
                                                </div>
                                            )}
                                        </div>
                                    ) : (
                                        <div className="flex items-center divide-x divide-gray-200">
                                            <Link 
                                                to="/settings"
                                                className="px-6 text-sm font-medium text-gray-600 hover:text-gray-900 transition-colors duration-200"
                                            >
                                                Settings
                                            </Link>
                                            <div className="flex items-center space-x-3 pl-6">
                                                <Link to="/login">
                                                    <Button variant="ghost" className="text-gray-600 hover:text-gray-900">
                                                        Login
                                                    </Button>
                                                </Link>
                                                <Link to="/signup">
                                                    <Button className="bg-blue-600 hover:bg-blue-700 text-white shadow-sm">
                                                        Sign Up
                                                    </Button>
                                                </Link>
                                            </div>
                                        </div>
                                    )}
                                </div>
                            </>
                        ) : (
                            <div className="flex items-center space-x-3">
                                <Link to="/login">
                                    <Button variant="ghost" className="text-gray-600 hover:text-gray-900">
                                        Login
                                                    </Button>
                                </Link>
                                <Link to="/signup">
                                    <Button className="bg-blue-600 hover:bg-blue-700 text-white shadow-sm">
                                        Sign Up
                                                    </Button>
                                </Link>
                            </div>
                        )}
                    </div>
                </div>
            </div>

            {/* Mobile menu - adjusted breakpoint */}
            <div
                className={`fixed inset-0 z-40 w-full h-full bg-white transform transition-transform ease-in-out duration-300 lg:hidden overflow-y-auto
                    ${isClosing || !isMobileMenuOpen ? '-translate-x-full' : 'translate-x-0'}`}
            >
                {/* Header section of mobile menu */}
                <div className="sticky top-0 bg-white border-b border-gray-100 px-4 py-4 flex items-center justify-between">
                    <Link
                        to="/subscriptions"
                        className="text-xl font-bold text-blue-600"
                        onClick={() => setIsMobileMenuOpen(false)}
                    >
                        {siteConfig.name}
                    </Link>
                    <button
                        type="button"
                        className="rounded-full p-2 hover:bg-gray-100 transition-colors duration-200"
                        onClick={() => setIsMobileMenuOpen(false)}
                        aria-label="Close menu"
                    >
                        <svg
                            className="h-6 w-6 text-gray-600"
                            fill="none"
                            viewBox="0 0 24 24"
                            strokeWidth="1.5"
                            stroke="currentColor"
                        >
                            <path strokeLinecap="round" strokeLinejoin="round" d="M6 18L18 6M6 6l12 12" />
                        </svg>
                    </button>
                </div>

                {/* Menu content */}
                <div className="px-4 py-6 space-y-8">
                    {/* User profile section if authenticated */}
                    {isAuthenticated && (
                        <>
                            <div className="pb-6 border-b border-gray-100">
                                <div className="flex items-center space-x-3">
                                    <div className="h-12 w-12 rounded-full bg-blue-100 flex items-center justify-center">
                                        <span className="text-blue-600 text-lg font-medium">
                                            {getFirstName(user?.name)?.charAt(0) || 'G'}
                                        </span>
                                    </div>
                                    <div className="flex flex-col">
                                        <span className="text-sm font-medium text-gray-900">
                                            {user?.name || 'Guest User'}
                                        </span>
                                        <span className="text-sm text-gray-500">
                                            {user?.email || ''}
                                        </span>
                                    </div>
                                </div>
                            </div>

                            {/* Currency selector in mobile menu */}
                            <div className="py-4 border-b border-gray-100">
                                <div className="flex items-center space-x-2">
                                    <span className="text-gray-500">
                                        {getCurrencySymbol(selectedCurrency)}
                                    </span>
                                    <Select
                                        value={selectedCurrency}
                                        onChange={handleCurrencyChange}
                                        className="flex-1"
                                        aria-label="Select currency"
                                    >
                                        {CURRENCIES.map(currency => (
                                            <option key={currency.code} value={currency.code}>
                                                {currency.code} ({currency.symbol}) - {currency.name}
                                            </option>
                                        ))}
                                    </Select>
                                </div>
                            </div>
                        </>
                    )}

                    {/* Navigation Links */}
                    <nav className="space-y-1">
                        {/* Show private links first if authenticated */}
                        {isAuthenticated && (
                            <>
                                {privateNavLinks.map((link) => (
                                    <Link
                                        key={link.to}
                                        to={link.to}
                                        className={`flex items-center px-4 py-3 text-base font-medium rounded-lg transition-colors duration-200 ${
                                            isActiveLink(link.to)
                                                ? 'bg-blue-50 text-blue-600'
                                                : 'text-gray-900 hover:bg-gray-50'
                                        }`}
                                        onClick={(e) => {
                                            e.preventDefault();
                                            handleNavigation(link.to);
                                        }}
                                    >
                                        {link.text}
                                    </Link>
                                ))}

                                {/* Add Settings link for authenticated users */}
                                <Link
                                    to="/settings"
                                    className={`flex items-center px-4 py-3 text-base font-medium rounded-lg transition-colors duration-200 ${isActiveLink('/settings')
                                        ? 'bg-blue-50 text-blue-600'
                                        : 'text-gray-900 hover:bg-gray-50'
                                        }`}
                                    onClick={(e) => {
                                        e.preventDefault();
                                        handleNavigation('/settings');
                                    }}
                                >
                                    Settings
                                </Link>
                            </>
                        )}

                        {/* Public links in a separate section */}
                        <div className="mt-6 pt-6 border-t border-gray-100">
                            <p className="px-4 text-sm font-medium text-gray-500 mb-2">More Information</p>
                            {publicNavLinks.map((link) => (
                                <Link
                                    key={link.to}
                                    to={link.to}
                                    className={`flex items-center px-4 py-3 text-base font-medium rounded-lg transition-colors duration-200 ${isActiveLink(link.to)
                                        ? 'bg-blue-50 text-blue-600'
                                        : 'text-gray-600 hover:bg-gray-50 hover:text-gray-900'
                                        }`}
                                    onClick={(e) => {
                                        e.preventDefault();
                                        handleNavigation(link.to);
                                    }}
                                >
                                    {link.text}
                                </Link>
                            ))}
                        </div>
                    </nav>

                    {/* Add informational section for non-authenticated users */}
                    {!isAuthenticated && (
                        <div className="py-6 space-y-6 border-t border-gray-100">
                            <div className="bg-blue-50 rounded-lg p-4">
                                <h3 className="text-sm font-medium text-blue-800 mb-2">
                                    Why Create an Account?
                                </h3>
                                <ul className="text-sm text-blue-700 space-y-2">
                                    <li className="flex items-center">
                                        <svg className="h-4 w-4 mr-2" fill="currentColor" viewBox="0 0 20 20">
                                            <path fillRule="evenodd" d="M10 18a8 8 0 100-16 8 8 0 000 16zm3.707-9.293a1 1 0 00-1.414-1.414L9 10.586 7.707 9.293a1 1 0 00-1.414 1.414l2 2a1 1 0 001.414 0l4-4z" clipRule="evenodd" />
                                        </svg>
                                        Access your personal dashboard
                                    </li>
                                    <li className="flex items-center">
                                        <svg className="h-4 w-4 mr-2" fill="currentColor" viewBox="0 0 20 20">
                                            <path fillRule="evenodd" d="M10 18a8 8 0 100-16 8 8 0 000 16zm3.707-9.293a1 1 0 00-1.414-1.414L9 10.586 7.707 9.293a1 1 0 00-1.414 1.414l2 2a1 1 0 001.414 0l4-4z" clipRule="evenodd" />
                                        </svg>
                                        Track your analytics
                                    </li>
                                    <li className="flex items-center">
                                        <svg className="h-4 w-4 mr-2" fill="currentColor" viewBox="0 0 20 20">
                                            <path fillRule="evenodd" d="M10 18a8 8 0 100-16 8 8 0 000 16zm3.707-9.293a1 1 0 00-1.414-1.414L9 10.586 7.707 9.293a1 1 0 00-1.414 1.414l2 2a1 1 0 001.414 0l4-4z" clipRule="evenodd" />
                                        </svg>
                                        Manage subscriptions
                                    </li>
                                </ul>
                            </div>
                        </div>
                    )}

                    {/* Feedback section */}
                    <div className="py-6 border-t border-gray-100">
                        <Feedback
                            position="bottom"
                            prompt="Help us improve our platform"
                            source="mobile_menu_feedback"
                            onSubmit={handleFeedbackSubmit}
                        />
                    </div>

                    {/* Auth Buttons */}
                    <div className="pt-6 border-t border-gray-100">
                        {isAuthenticated && !user?.is_anonymous ? (
                            <Button
                                variant="ghost"
                                onClick={() => {
                                    handleMenuClose(() => logout());
                                }}
                                className="w-full justify-center bg-gray-50 hover:bg-gray-100 text-gray-900"
                            >
                                Logout
                            </Button>
                        ) : (
                            <div className="space-y-3">
                                <Link
                                    to="/login"
                                    className="block w-full"
                                    onClick={(e) => {
                                        e.preventDefault();
                                        handleNavigation('/login');
                                    }}
                                >
                                    <Button variant="ghost" className="w-full justify-center bg-gray-50 hover:bg-gray-100 text-gray-900">
                                        Login
                                                    </Button>
                                </Link>
                                <Link
                                    to="/signup"
                                    className="block w-full"
                                    onClick={(e) => {
                                        e.preventDefault();
                                        handleNavigation('/signup');
                                    }}
                                >
                                    <Button className="w-full justify-center">
                                        Sign Up
                                                    </Button>
                                </Link>
                            </div>
                        )}
                    </div>
                </div>
            </div>
        </header>
    );
};